import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { X, Cookie, ChevronDown, ChevronUp } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'zenstudios_cookie_consent';

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always enabled
    analytics: false,
    marketing: false,
    functional: false
  });

  useEffect(() => {
    // Check if consent has been given
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Delay showing banner for better UX
      const timer = setTimeout(() => setVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (consentData) => {
    const consentRecord = {
      ...consentData,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentRecord));
    setVisible(false);
  };

  const handleAcceptAll = () => {
    saveConsent({
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    });
  };

  const handleRejectAll = () => {
    saveConsent({
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    });
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return; // Can't toggle necessary cookies
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  if (!visible) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-[100] bg-white border-t border-gray-200 shadow-2xl"
      data-testid="cookie-consent-banner"
    >
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        <div className="flex items-start gap-4">
          <div className="hidden md:flex items-center justify-center w-12 h-12 bg-[#15803D]/10 rounded-full flex-shrink-0">
            <Cookie className="w-6 h-6 text-[#15803D]" />
          </div>
          
          <div className="flex-1">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Cookie Preferences</h3>
                <p className="text-sm text-gray-600">
                  We use cookies to enhance your browsing experience, provide personalized content, 
                  and analyze our traffic. You can customize your preferences below.
                </p>
              </div>
              <button 
                onClick={handleRejectAll}
                className="text-gray-400 hover:text-gray-600 p-1"
                aria-label="Close"
                data-testid="cookie-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Customize Panel */}
            <div className="mb-4">
              <button
                onClick={() => setShowCustomize(!showCustomize)}
                className="flex items-center gap-2 text-sm text-[#15803D] hover:text-[#166534] font-medium"
                data-testid="cookie-customize-toggle"
              >
                {showCustomize ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                Customize Preferences
              </button>

              {showCustomize && (
                <div className="mt-4 space-y-3 bg-gray-50 p-4 rounded-lg" data-testid="cookie-customize-panel">
                  {/* Necessary Cookies */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 text-sm">Necessary Cookies</p>
                      <p className="text-xs text-gray-500">Required for basic website functionality</p>
                    </div>
                    <div className="relative">
                      <input
                        type="checkbox"
                        checked={true}
                        disabled
                        className="sr-only"
                      />
                      <div className="w-10 h-6 bg-[#15803D] rounded-full cursor-not-allowed opacity-75">
                        <div className="absolute right-1 top-1 bg-white w-4 h-4 rounded-full shadow" />
                      </div>
                    </div>
                  </div>

                  {/* Analytics Cookies */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 text-sm">Analytics Cookies</p>
                      <p className="text-xs text-gray-500">Help us understand how visitors interact with our website</p>
                    </div>
                    <button
                      onClick={() => togglePreference('analytics')}
                      className="relative"
                      data-testid="cookie-analytics-toggle"
                    >
                      <div className={`w-10 h-6 rounded-full transition-colors ${preferences.analytics ? 'bg-[#15803D]' : 'bg-gray-300'}`}>
                        <div className={`absolute top-1 bg-white w-4 h-4 rounded-full shadow transition-transform ${preferences.analytics ? 'right-1' : 'left-1'}`} />
                      </div>
                    </button>
                  </div>

                  {/* Marketing Cookies */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 text-sm">Marketing Cookies</p>
                      <p className="text-xs text-gray-500">Used to deliver personalized advertisements</p>
                    </div>
                    <button
                      onClick={() => togglePreference('marketing')}
                      className="relative"
                      data-testid="cookie-marketing-toggle"
                    >
                      <div className={`w-10 h-6 rounded-full transition-colors ${preferences.marketing ? 'bg-[#15803D]' : 'bg-gray-300'}`}>
                        <div className={`absolute top-1 bg-white w-4 h-4 rounded-full shadow transition-transform ${preferences.marketing ? 'right-1' : 'left-1'}`} />
                      </div>
                    </button>
                  </div>

                  {/* Functional Cookies */}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900 text-sm">Functional Cookies</p>
                      <p className="text-xs text-gray-500">Enable enhanced functionality and personalization</p>
                    </div>
                    <button
                      onClick={() => togglePreference('functional')}
                      className="relative"
                      data-testid="cookie-functional-toggle"
                    >
                      <div className={`w-10 h-6 rounded-full transition-colors ${preferences.functional ? 'bg-[#15803D]' : 'bg-gray-300'}`}>
                        <div className={`absolute top-1 bg-white w-4 h-4 rounded-full shadow transition-transform ${preferences.functional ? 'right-1' : 'left-1'}`} />
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={handleRejectAll}
                variant="outline"
                className="rounded-none border-gray-300 hover:bg-gray-50"
                data-testid="cookie-reject-all-btn"
              >
                Reject All
              </Button>
              {showCustomize && (
                <Button
                  onClick={handleSavePreferences}
                  variant="outline"
                  className="rounded-none border-[#15803D] text-[#15803D] hover:bg-[#15803D]/5"
                  data-testid="cookie-save-preferences-btn"
                >
                  Save Preferences
                </Button>
              )}
              <Button
                onClick={handleAcceptAll}
                className="rounded-none bg-[#15803D] hover:bg-[#166534] text-white"
                data-testid="cookie-accept-all-btn"
              >
                Accept All
              </Button>
            </div>

            {/* Privacy Policy Link */}
            <p className="mt-3 text-xs text-gray-500">
              By using our website, you agree to our{' '}
              <a href="/privacy-policy" className="text-[#15803D] hover:underline">Privacy Policy</a>
              {' '}and{' '}
              <a href="/terms-conditions" className="text-[#15803D] hover:underline">Terms of Service</a>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
